import React, { useState } from "react";
import { ArrowUpRight, Users, HandHeart, Sparkles } from "lucide-react";
import { toast } from "sonner";
import SectionHeader from "@/components/site/SectionHeader";
import { submitPartnership } from "@/lib/api";

const SEEKING = [
  { icon: Users, title: "Schools & kura", text: "Curriculum-aligned collaborations to bring sexual health and wellbeing education into classrooms." },
  { icon: HandHeart, title: "Iwi & community groups", text: "Marae-based and community-grounded wānanga shaped by local voices and tikanga." },
  { icon: Sparkles, title: "Health providers & NGOs", text: "Joined-up pathways so rangatahi can access the right clinical and social support." },
];

export default function Partners() {
  const [form, setForm] = useState({ organisation: "", contact_name: "", email: "", role: "", partnership_type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitPartnership(form);
      toast.success("Enquiry sent — we'll be in touch shortly.");
      setForm({ organisation: "", contact_name: "", email: "", role: "", partnership_type: "", message: "" });
    } catch {
      toast.error("Couldn't send enquiry. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <>
      <section className="container-x pt-20 pb-16 sm:pt-28">
        <div className="max-w-4xl">
          <span className="label-eyebrow">— Community &amp; Collaboration</span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-7xl font-heading font-semibold tracking-tighter leading-[1.02] text-balance">
            Currently building community partnerships across Aotearoa.
          </h1>
          <p className="mt-8 text-lg text-ink/65 max-w-2xl leading-relaxed">
            We are engaging with organisations and stakeholders across the youth wellbeing and public health sector. As an
            emerging charitable trust, we welcome conversations with anyone who shares our kaupapa.
          </p>
        </div>
      </section>

      <section className="container-x pb-12">
        <div className="bg-sand rounded-3xl p-10 sm:p-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="label-eyebrow">— Where we are right now</span>
              <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-heading font-semibold leading-tight text-balance">
                Currently building community partnerships.
              </h2>
              <p className="mt-5 text-ink/70 leading-relaxed">
                We are at the early stages of building meaningful relationships across the sector. We do not claim formal
                partnerships or endorsements at this time — instead, we are listening, learning, and developing the
                collaborations that will shape our work.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="bg-white border border-stone rounded-2xl p-7">
                <span className="label-eyebrow">— Charitable Trust</span>
                <p className="mt-3 font-heading text-lg text-ink">CC58012</p>
                <p className="mt-2 text-sm text-ink/60">Founded by Shailesh Bagwe · Tāmaki Makaurau, Auckland</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-y">
        <div className="container-x">
          <SectionHeader
            eyebrow="Seeking collaborators across Aotearoa"
            title="Who we'd love to hear from."
            description="If your work touches the lives of rangatahi, we'd love to kōrero. Collaboration takes many forms."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {SEEKING.map(({ icon: Icon, title, text }) => (
              <article key={title} className="card-surface">
                <Icon className="w-6 h-6 text-crimson mb-5" />
                <h3 className="font-heading font-semibold text-lg leading-snug">{title}</h3>
                <p className="mt-3 text-ink/65 text-sm leading-relaxed">{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink text-bone" id="enquiry">
        <div className="container-x py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <span className="label-eyebrow">— Collaboration enquiry</span>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-heading font-semibold tracking-tight text-balance">
                Let's start a kōrero.
              </h2>
              <p className="mt-6 text-bone/65 leading-relaxed">
                Tell us a little about your work and what you'd like to explore. We respond to every enquiry personally.
              </p>
            </div>
            <form onSubmit={onSubmit} className="lg:col-span-7 bg-white text-ink rounded-3xl p-8 sm:p-10 border border-stone" data-testid="partnership-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <label className="text-sm font-medium text-ink/80 mb-2 block">Organisation</label>
                  <input required className="input-field" value={form.organisation} onChange={onChange("organisation")} data-testid="partner-organisation" />
                </div>
                <div>
                  <label className="text-sm font-medium text-ink/80 mb-2 block">Contact name</label>
                  <input required className="input-field" value={form.contact_name} onChange={onChange("contact_name")} data-testid="partner-contact-name" />
                </div>
                <div>
                  <label className="text-sm font-medium text-ink/80 mb-2 block">Email</label>
                  <input required type="email" className="input-field" value={form.email} onChange={onChange("email")} data-testid="partner-email" />
                </div>
                <div>
                  <label className="text-sm font-medium text-ink/80 mb-2 block">Your role</label>
                  <input className="input-field" value={form.role} onChange={onChange("role")} data-testid="partner-role" />
                </div>
                <div>
                  <label className="text-sm font-medium text-ink/80 mb-2 block">Type of collaboration</label>
                  <select className="input-field" value={form.partnership_type} onChange={onChange("partnership_type")} data-testid="partner-type">
                    <option value="">Select...</option>
                    <option>School / kura</option>
                    <option>Iwi / hapū</option>
                    <option>Health provider</option>
                    <option>NGO / community</option>
                    <option>Funder / sponsor</option>
                    <option>Other</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm font-medium text-ink/80 mb-2 block">Tell us more</label>
                  <textarea required rows={5} className="input-field resize-none" value={form.message} onChange={onChange("message")} data-testid="partner-message" />
                </div>
              </div>
              <button type="submit" disabled={loading} className="mt-7 btn-primary" data-testid="partner-submit">
                {loading ? "Sending..." : <>Submit enquiry <ArrowUpRight className="w-4 h-4" /></>}
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
